/***
 * PROPS:
 *      stateLabels ARRAY of STRINGs for each state (in consecutive order)
 *      stateColors ARRAY of STRINGs CSS colors (one per state)
 *      stageLabels ARRAY of STRINGs
 *      demographics ARRAY of ARRAYS of STRINGs (at least 1 required)
 *         first ARRAY gives names of demographic categories
 *         Each successive ARRAY gives list of categories for corresponding demographic
 *      demographicColors ARRAY of ARRAY of STRINGs CSS colors for each demographic category
 *      data ARRAY of ARRAY of INTEGERs indicating movement of patients from node to node by demographic category
 *              demCat, demVal, clusterID, number, state0, …, stateN-1
 *              If <demCat> === -1, then it is for an unsegmented node (and <demVal> must = 0)
 *              If <state>X === -1, then patients have dropped out of study
 ***/

import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'

import { StagedFlowControls } from './staged-flow-controls'
import { Segments } from './segments'
import { Flows } from './flows'
import { SelectedSegments } from './selected-segments'
import { SelectedFlows } from './selected-flows'
import { Axis } from './axis'
import { ControlBar } from './control-bar'
import { Caption } from '../../common/caption/caption'
import { TooltipContextProvider } from '../../common/tooltip/tooltip.context'
import { Tooltip } from '../../common/tooltip/tooltip'
import { sankeyService } from './sankey.service'

import { useStyles } from './sankey.style'

export const SankeyContainer = function (props = {}) {
  const {
    children,
    data,
    datasets,
    stateLabels,
    stageLabels,
    stateColors,
    demographicColors,
    valueLabel,
    axisLabel,
    width,
    height
  } = props

  const [sankeyDatasets, setSankeyDatasets] = useState()
  const [selectedDemographicCategory, setSelectedDemographicCategory] = useState(-1) // current demographic category
  const [shouldShowFlows, setShouldShowFlows] = useState(true)
  const [shouldShowSelected, setShouldShowSelected] = useState(false)

  useEffect(() => {
    if (data || (datasets && datasets.length)) {
      selectDemographicCategory(-1)
    }
  }, [data, datasets])

  useEffect(() => {
    if (data || (datasets && datasets.length)) {
      selectDemographicCategory(selectedDemographicCategory)
    }
  }, [stageLabels, stateLabels, stateColors, demographicColors, valueLabel, axisLabel, width, height])

  function selectDemographicCategory (demographicCategory) {
    const dataList = datasets || [data]
    const newDatasets = []
    dataList.forEach((data) => {
      newDatasets.push(sankeyService.getSankeyData(data, demographicCategory, {
        stageLabels,
        stateLabels,
        width,
        height,
        stateColors,
        demographicColors,
        valueLabel,
        axisLabel
      }))
    })
    setSankeyDatasets(newDatasets)
    setSelectedDemographicCategory(demographicCategory)
  }

  function handleDocumentClick () {
    document.removeEventListener('mousedown', handleDocumentClick)

    const newDatasets = [...sankeyDatasets]
    sankeyDatasets.forEach((sankeyData) => {
      sankeyService.deselectSegment(sankeyData)
      delete sankeyData.selectedSegmentSize
    })
    setSankeyDatasets(newDatasets)

    setShouldShowFlows(true)
    setShouldShowSelected(false)
  }

  function onClickSegment (stage, state, demographicValue) {
    const newDatasets = [...sankeyDatasets]
    sankeyDatasets.forEach((sankeyData) => {
      const selectedSegment = sankeyService.selectSegment(sankeyData, stage, state, demographicValue)
      sankeyData.selectedSegmentSize = selectedSegment.size
    })
    setSankeyDatasets(newDatasets)

    setShouldShowSelected(true)
    setShouldShowFlows(false)

    document.addEventListener('mousedown', handleDocumentClick)
  }

  return (
    <>
      {children({
        sankeyDatasets,
        selectedDemographicCategory,
        onStagedFlowSelect: selectDemographicCategory,
        handleDocumentClick,
        onClickSegment,
        shouldShowFlows,
        shouldShowSelected
      })}
    </>
  )
}

export const Sankey = function (props = {}) {
  const {
    stateLabels,
    demographics,
    demographicColors,
    caption
  } = props

  return (
    <SankeyContainer {...props}>
      {({
        sankeyDatasets,
        onStagedFlowSelect,
        ...sankeyProps
      }) => sankeyDatasets && sankeyDatasets && sankeyDatasets.length ? sankeyDatasets.map((sankeyData, i) => (
        <div key={i}>
          <StagedFlowControls demographics={demographics} demographicColors={demographicColors} onSelect={onStagedFlowSelect} />
          <ControlBar stateLabels={stateLabels} totalSize={sankeyData.getStageTotalSize(0)} valueLabel={sankeyData.layout.valueLabel} stateColors={sankeyData.layout.stateColors} />
          <SingleSankey sankeyData={sankeyDatasets[0]} caption={caption} {...sankeyProps} />
        </div>
      )) : null}
    </SankeyContainer>
  )
}

export function SingleSankey ({ sankeyData, caption, selectedDemographicCategory, onClickSegment, shouldShowFlows, shouldShowSelected }) {
  const classes = useStyles()

  return (
    <TooltipContextProvider>
      <div data-qa="staged-flow-wrapper">
        <svg width={sankeyData.layout.width} height={sankeyData.layout.height} className={classes.svg} data-qa="staged-flow-chart">
          <Axis sankeyData={sankeyData} />
          {shouldShowFlows && <Flows sankeyData={sankeyData} />}
          <Segments sankeyData={sankeyData} selectedDemographicCategory={selectedDemographicCategory}
            isDisabled={shouldShowSelected} onClickSegment={onClickSegment} />
          {shouldShowSelected &&
            <>
              <SelectedFlows sankeyData={sankeyData} selectedSegmentSize={sankeyData.selectedSegmentSize} />
              <SelectedSegments sankeyData={sankeyData} selectedDemographicCategory={selectedDemographicCategory} selectedSegmentSize={sankeyData.selectedSegmentSize} />
            </>
          }
        </svg>
      </div>
      {caption && (<Caption>{caption}</Caption>)}
      <Tooltip defaults={{ primaryLabel: sankeyData.layout.valueLabel }} />
    </TooltipContextProvider>
  )
}

export function sankeyWidget (id, { data, stateLabels, stageLabels, stateColors, demographics, demographicColors, valueLabel, axisLabel, caption, width, height }) {
  const element = document.getElementById(id)
  ReactDOM.render(React.createElement(Sankey, { data, stateLabels, stageLabels, stateColors, demographics, demographicColors, valueLabel, axisLabel, caption, width, height }), element)
}
